import { Box, Collapse, Stack, Switch, TextField, Typography } from '@mui/material';
import { ACCENT } from '../_shared';
import type { WizardData } from './wizardTypes';

interface Props {
    data: WizardData;
    onChange: (patch: Partial<WizardData>) => void;
    compact?: boolean;
}

const Step5Message = ({ data, onChange, compact }: Props) => {
    const isComment = data.triggerType === 'COMMENT_KEYWORD';

    return (
        <Box>
            {!compact && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                        DM에 어떤 메시지를 보낼까요?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                        실제로 사용자에게 전달될 메시지를 작성해주세요.
                        <br />
                        버튼과 링크를 함께 넣을 수 있어요.
                    </Typography>
                </>
            )}

            <Stack spacing={2.5}>
                {isComment && (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                            댓글 공개 답글 (선택)
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder='예) "DM 확인해주세요! 📬"'
                            value={data.publicReply}
                            onChange={(ev) => onChange({ publicReply: ev.target.value })}
                            helperText="댓글에 자동으로 달리는 공개 답글이에요. 비워두면 답글 없이 DM만 보냅니다."
                            inputProps={{ maxLength: 200 }}
                        />
                    </Box>
                )}

                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                        DM 본문 *
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder={'안녕하세요! 요청하신 자료를 보내드려요 ✨\n\n아래 버튼을 눌러 받아보세요.'}
                        value={data.responseMessage}
                        onChange={(ev) => onChange({ responseMessage: ev.target.value })}
                        inputProps={{ maxLength: 1000 }}
                    />
                </Box>

                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                        CTA 버튼 (선택)
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                            sx={{ flex: 1 }}
                            placeholder="버튼 텍스트 (예: 가이드 받기)"
                            value={data.buttonText}
                            onChange={(ev) => onChange({ buttonText: ev.target.value })}
                            inputProps={{ maxLength: 50 }}
                        />
                        <TextField
                            sx={{ flex: 2 }}
                            placeholder="https://example.com"
                            value={data.buttonLink}
                            onChange={(ev) => onChange({ buttonLink: ev.target.value })}
                            type="url"
                        />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        버튼 텍스트와 링크는 함께 입력하거나, 둘 다 비워두세요.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                팔로워만 발송
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                내 계정을 팔로우 중인 사용자에게만 DM 을 자동 발송합니다
                            </Typography>
                        </Box>
                        <Switch
                            checked={data.requireFollow}
                            onChange={(_, checked) => onChange({ requireFollow: checked })}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT },
                            }}
                        />
                    </Stack>
                    <Collapse in={data.requireFollow}>
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                            <TextField
                                size="small"
                                label="팔로우 확인 메시지 (선택)"
                                placeholder="예) 잠시만요, 팔로우 여부 확인 중이에요"
                                value={data.followCheckMessage}
                                onChange={(ev) => onChange({ followCheckMessage: ev.target.value })}
                                inputProps={{ maxLength: 200 }}
                            />
                            <TextField
                                size="small"
                                label="팔로우 안 한 사용자 안내 (선택)"
                                placeholder="예) 먼저 팔로우 부탁드려요 💌"
                                value={data.notFollowingMessage}
                                onChange={(ev) => onChange({ notFollowingMessage: ev.target.value })}
                                inputProps={{ maxLength: 200 }}
                            />
                        </Stack>
                    </Collapse>
                </Box>
            </Stack>
        </Box>
    );
};

export default Step5Message;
